import { FC, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Replace Next.js hooks
import {
    JobFacet,
    JobFilterType,
    JobSelectedFilter,
    PublishJobFilterItem
} from 'Types';
import {
    SearchFilterBox,
    JobFilterDistanceRange,
    JobFilterMetadata
} from 'Elements';
import { pascalToCamel } from 'containers/Utils';
import JobDynamicFilter from './dynamic-filter/JobDynamicFilter';

interface Props {
    list?: JobFacet[];
    filters?: JobSelectedFilter;
    isLoading?: boolean;
    showDistance?: boolean;
    onReset: (() => void) | false;
    visibleItems?: number;
    className?: string;
    setPageNumber?: any;
}

export const JobFilters: FC<Props> = ({
    list,
    isLoading,
    showDistance = true,
    onReset,
    visibleItems,
    className,
    filters,
    setPageNumber
}) => {
    const location = useLocation();
    const navigate = useNavigate();

    // const query = new URLSearchParams(location.search);

    const setSearchParams = (params: Record<string, string>) => {
        const updatedQuery = new URLSearchParams(location.search);
        Object.keys(params).forEach((key) => {
            updatedQuery.set(key, params[key]);
        });
        navigate({ search: updatedQuery.toString() }, { replace: true });
    };

    const clearSearchParams = (keys: string[]) => {
        const updatedQuery = new URLSearchParams(location.search);
        keys.forEach((key) => {
            updatedQuery.delete(key);
        });
        navigate({ search: updatedQuery.toString() }, { replace: true });
    };

    const checkSelected = useCallback(
        (value: string, filter: JobFilterType) => {
            return (
                (filters?.[
                    pascalToCamel(
                        filter.split('/')[0]
                    ) as keyof JobSelectedFilter
                ] as string[])?.includes(value) ?? false
            );
        },
        [filters]
    );

    const getUpdatedList = useCallback(
        (filter: JobFilterType) => {
            const items = list?.filter((item) => item.facet === filter)[0];
            items?.items?.forEach((item) => {
                item['isChecked'] = checkSelected(
                    item.value.split('|')[0],
                    filter
                );
            });
            return items?.items;
        },
        [list, checkSelected]
    );

    const handleApplyFilter = (
        param: JobFilterType,
        selected_filters: PublishJobFilterItem[]
    ) => {
        const filter_values = selected_filters
            .map((filter) => filter.value.split('|')[0])
            .join(',');
        if (filter_values) {
            setSearchParams({
                page: '1',
                [param.split('/')[0]]: filter_values
            });
            setPageNumber && setPageNumber(1);
        } else {
            clearSearchParams([param.split('/')[0]]);
        }
    };

    const filterBox: JobFilterType[] = [
        'JobActivities/Title',
        'Locations/Title',
        'BusinessSectors/Title'
    ];

    return (
        <SearchFilterBox
            onReset={onReset}
            isLoading={isLoading}
            className={className}>
            {showDistance && (
                <JobFilterDistanceRange setPageNumber={setPageNumber} />
            )}
            <JobDynamicFilter
                list={list}
                filters={filters}
                visibleItems={visibleItems}
                setPageNumber={setPageNumber}
            />
            {filterBox.map((filter: any, idx: number) => (
                <JobFilterMetadata
                    key={`${filter}_${idx}`}
                    type={filter}
                    list={getUpdatedList(filter) as PublishJobFilterItem[]}
                    onApplyFilter={(selected) =>
                        handleApplyFilter(filter, selected)
                    }
                    visibleItems={visibleItems}
                />
            ))}
        </SearchFilterBox>
    );
};